// src/pages/comercial/Objetivos.js
import React, { useEffect, useMemo, useState, useRef } from "react";
import { useTable, useFilters, useSortBy } from "react-table";

import api, { handleApi } from "pages/api/api";
import { useAlert } from "pages/context/AlertContext";
import { useAuth } from "pages/context/AuthContext";
import PageHeader from "pages/components/PageHeader";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import Draggable from "react-draggable";
import Paper from "@mui/material/Paper";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import Card from "@mui/material/Card";
import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Divider from "@mui/material/Divider";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import TableViewIcon from "@mui/icons-material/TableView";

import CriarObjetivos from "pages/comercial/CriarObjetivos";

function PaperComponent(props) {
  return (
    <Draggable
      handle=".draggable-dialog-title"
      cancel={'[class*="MuiDialogContent-root"], input, textarea, select, button'}
    >
      <Paper {...props} />
    </Draggable>
  );
}

// ------------------- Filtros por coluna -------------------
function TextFilter({ column: { filterValue, setFilter } }) {
  return (
    <input
      value={filterValue || ""}
      onChange={(e) => setFilter(e.target.value || undefined)}
      placeholder="filtrar..."
      style={{
        width: "100%",
        padding: "6px 4px",
        border: "none",
        borderBottom: "1px solid rgba(0,0,0,0.2)",
        outline: "none",
        background: "transparent",
        fontSize: 12,
        color: "#556",
      }}
      onFocus={(e) => (e.target.style.borderBottom = "1px solid rgba(0,0,0,0.35)")}
      onBlur={(e) => (e.target.style.borderBottom = "1px solid rgba(0,0,0,0.2)")}
    />
  );
}

const safeNum = (v) => {
  if (v === null || v === undefined) return 0;
  if (typeof v === "number") return Number.isFinite(v) ? v : 0;

  const s = String(v).trim();
  if (!s) return 0;

  // suporta PT "87,12"
  const n = Number(s.replace(/\s/g, "").replace(",", "."));
  return Number.isFinite(n) ? n : 0;
};

const pickMetricKey = (obj) => {
  if (!obj) return "qtd";

  if (safeNum(obj.qtd) > 0) return "qtd";
  if (safeNum(obj.euros) > 0) return "euros";
  if (safeNum(obj.litros) > 0) return "litros";
  if (safeNum(obj.clientes) > 0) return "clientes";

  return "qtd";
};

const metricLabel = (k) => {
  switch (k) {
    case "qtd":
      return "qtd";
    case "euros":
      return "euros";
    case "litros":
      return "litros";
    case "clientes":
      return "clientes";
    default:
      return "qtd";
  }
};

function pickMetricKeyFromDetalhes(det, fallback = "qtd") {
  const keys = ["qtd", "euros", "litros", "clientes"];

  // 1) métrica com OBJETIVO > 0 em alguma secção (fabricante/equipa/individuais)
  for (const k of keys) {
    const objFab = safeNum(det?.fabricante?.[k]?.objetivo);
    const objEq = safeNum(det?.equipa?.[k]?.objetivo);
    const objInd = safeNum(det?.individuais?.[k]?.objetivo);

    if (objFab > 0 || objEq > 0 || objInd > 0) return k;
  }

  // 2) fallback: se objetivo for 0 (casos especiais), usa a primeira com REAL/VENDAS > 0
  for (const k of keys) {
    const venFab = safeNum(det?.fabricante?.[k]?.vendas);
    const venEq = safeNum(det?.equipa?.[k]?.vendas);
    const venInd = safeNum(det?.individuais?.[k]?.vendas);

    if (venFab > 0 || venEq > 0 || venInd > 0) return k;
  }

  return fallback;
}

const normTxt = (v) =>
  String(v ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();

const incluiTxt = (valor, filtro) => {
  const f = normTxt(filtro);
  if (!f) return true;
  return normTxt(valor).includes(f);
};

function numFmt(v) {
  if (v === null || v === undefined || v === "") return "";
  const n = Number(v);
  if (Number.isNaN(n)) return "";
  return n.toLocaleString("pt-PT", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

function valoresResumo(row) {
  const parts = [];
  if (Number(row.qtd) > 0) parts.push(`QTD ${numFmt(row.qtd)}`);
  if (Number(row.euros) > 0) parts.push(`€ ${numFmt(row.euros)}`);
  if (Number(row.litros) > 0) parts.push(`L ${numFmt(row.litros)}`);
  if (Number(row.clientes) > 0) parts.push(`Cli ${numFmt(row.clientes)}`);
  return parts.join(" | ");
}

function TruncTooltipCell({ value, maxWidth = 220 }) {
  const [open, setOpen] = useState(false);
  const txt = (value ?? "").toString().trim();

  if (!txt) return <span />;

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <span>
        <Tooltip
          title={txt}
          arrow
          placement="top"
          open={open}
          disableHoverListener
          disableFocusListener
          disableTouchListener
        >
          <span
            onMouseEnter={() => setOpen(true)}
            onMouseLeave={() => setOpen(false)}
            onClick={() => setOpen((v) => !v)} // tablet/click
            style={{
              display: "inline-block",
              maxWidth,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              verticalAlign: "middle",
              cursor: "pointer",
            }}
          >
            {txt}
          </span>
        </Tooltip>
      </span>
    </ClickAwayListener>
  );
}

const dateOnly = (v) => {
  if (!v) return "";
  return String(v).split("T")[0];
};

async function enriquecerObjetivoCache(obj) {
  if (!obj) return null;

  let met = pickMetricKey(obj);

  const semMetricaBase =
    safeNum(obj?.qtd) <= 0 &&
    safeNum(obj?.euros) <= 0 &&
    safeNum(obj?.litros) <= 0 &&
    safeNum(obj?.clientes) <= 0;

  if (semMetricaBase && obj?.id) {
    try {
      const [det] = await handleApi(api.post("Comercial/SelObjetivoDetalhesCache", { id: obj.id }));
      if (det) {
        met = pickMetricKeyFromDetalhes(det, met);
      }
    } catch {
      // fallback silencioso
    }
  }

  return {
    ...obj,
    _metricKey: met,
  };
}

function hasAnyObjectiveSec(secData) {
  if (!secData) return false;
  const keys = ["qtd", "euros", "litros", "clientes"];
  return keys.some((k) => Number(secData?.[k]?.objetivo ?? 0) > 0);
}

function hasAnyObjectiveVendedores(vendedores, tipoKey) {
  const arr = Array.isArray(vendedores) ? vendedores : [];
  return arr.some((v) => Number(v?.[tipoKey]?.objetivo ?? 0) > 0);
}

function metricOrZero(m) {
  return {
    objetivo: Number(m?.objetivo ?? 0),
    vendas: Number(m?.vendas ?? 0),
  };
}

function difFmt(objetivo, real) {
  const o = Number(objetivo ?? 0);
  const r = Number(real ?? 0);
  if (!o) return "-";
  return numFmt(r - o);
}

function pctFmtAtingimento(objetivo, real) {
  const o = Number(objetivo ?? 0);
  const r = Number(real ?? 0);
  if (!o) return "-";
  const pct = (r / o) * 100;
  return `${pct.toLocaleString("pt-PT", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  })}%`;
}

function difCell(objetivo, real) {
  const o = Number(objetivo ?? 0);
  const r = Number(real ?? 0);

  if (!o) return <span>-</span>;

  const dif = r - o;
  const color = dif > 0 ? "#4caf50" : dif < 0 ? "#d32f2f" : undefined;

  return <span style={{ color, fontWeight: 500 }}>{numFmt(dif)}</span>;
}

function SecResumoTable({ title, secData, enableRealClick = false, onRealClick }) {
  const rows = useMemo(() => {
    const tipos = [
      { tipo: "Qtd", key: "qtd" },
      { tipo: "Euros", key: "euros" },
      { tipo: "Litros", key: "litros" },
      { tipo: "Clientes", key: "clientes" },
    ];

    return tipos.map((t) => {
      const m = metricOrZero(secData?.[t.key]);
      return {
        key: t.key,
        tipo: t.tipo,
        objetivo: m.objetivo,
        vendas: m.vendas,
      };
    });
  }, [secData]);

  const columns = useMemo(
    () => [
      { Header: "Tipo", accessor: "tipo" },
      {
        Header: "Objetivo",
        accessor: "objetivo",
        Cell: ({ value, row }) => {
          const mk = pickMetricKey(row.original);
          return `${value} (${metricLabel(mk)})`;
        },
      },
      {
        Header: "Real",
        accessor: "vendas",
        Cell: ({ value, row }) => {
          const txt = numFmt(value);

          // sem "info": só mostra o número (como antes, mas sem clique)
          if (!enableRealClick) return <strong>{txt}</strong>;

          return (
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <strong>{txt}</strong>

              <Tooltip title="Ver detalhe por vendedor" arrow placement="top">
                <IconButton
                  size="small"
                  color="info"
                  onClick={() => onRealClick?.(row.original.key, row.original.tipo)}
                  sx={{ p: 0.25 }}
                >
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </div>
          );
        },
      },
      {
        Header: "Dif.",
        id: "dif",
        Cell: ({ row }) => difCell(row.original.objetivo, row.original.vendas),
      },
      {
        Header: "%",
        id: "pct",
        Cell: ({ row }) => pctFmtAtingimento(row.original.objetivo, row.original.vendas),
      },
    ],
    [enableRealClick, onRealClick]
  );

  const table = useTable({ columns, data: rows }, useSortBy);

  return (
    <div style={{ marginBottom: 18 }}>
      <MDTypography
        variant="button"
        fontWeight="medium"
        color="text"
        sx={{ display: "block", mb: 1 }}
      >
        {title}
      </MDTypography>

      <div style={{ width: "100%", overflowX: "auto" }}>
        <table {...table.getTableProps()} style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            {table.headerGroups.map((hg) => (
              <tr {...hg.getHeaderGroupProps()} key={hg.id}>
                {hg.headers.map((col) => (
                  <th
                    {...col.getHeaderProps(col.getSortByToggleProps())}
                    key={col.id}
                    style={{
                      textAlign: "left",
                      padding: "10px 8px",
                      borderBottom: "1px solid rgba(0,0,0,0.12)",
                      fontSize: 12,
                      color: "#344767",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span>{col.render("Header")}</span>
                      <span style={{ fontSize: 11, opacity: 0.7 }}>
                        {col.isSorted ? (col.isSortedDesc ? "▼" : "▲") : ""}
                      </span>
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody {...table.getTableBodyProps()}>
            {table.rows.map((row) => {
              table.prepareRow(row);
              return (
                <tr
                  {...row.getRowProps()}
                  key={row.id}
                  style={{ borderBottom: "1px solid rgba(0,0,0,0.06)" }}
                >
                  {row.cells.map((cell) => (
                    <td
                      {...cell.getCellProps()}
                      key={cell.column.id}
                      style={{
                        padding: "10px 8px",
                        fontSize: 13,
                        color: "#344767",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {cell.render("Cell")}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function VendedoresTable({ vendedores, tipoKey, onVerVendas }) {
  const rows = useMemo(() => {
    const arr = Array.isArray(vendedores) ? vendedores : [];
    return arr.map((v) => {
      const m = metricOrZero(v?.[tipoKey]);
      return {
        vendedor: v?.vendedor ?? "",
        refvendedor: v?.refvendedor ?? "",
        objetivo: m.objetivo,
        vendas: m.vendas,
      };
    });
  }, [vendedores, tipoKey]);

  const columns = useMemo(
    () => [
      {
        Header: "Vendedor",
        accessor: "vendedor",
        Cell: ({ row }) => <span title={row.original.refvendedor}>{row.original.vendedor}</span>,
      },
      {
        Header: "Objetivo",
        accessor: "objetivo",
        Cell: ({ value }) => (Number(value) === 0 ? "-" : numFmt(value)),
      },
      {
        Header: "Real",
        accessor: "vendas",
        Cell: ({ value, row }) => (
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <strong>{numFmt(value)}</strong>

            <Tooltip title="Ver vendas deste vendedor" arrow>
              <IconButton
                size="small"
                color="info"
                onClick={() => onVerVendas?.(row.original.refvendedor)}
              >
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
        ),
      },
      {
        Header: "Dif.",
        id: "dif",
        Cell: ({ row }) => difCell(row.original.objetivo, row.original.vendas),
      },
      {
        Header: "%",
        id: "pct",
        Cell: ({ row }) => pctFmtAtingimento(row.original.objetivo, row.original.vendas),
      },
    ],
    [onVerVendas]
  );

  const table = useTable({ columns, data: rows }, useSortBy);

  return (
    <div>
      <div style={{ width: "100%", overflowX: "auto" }}>
        <table {...table.getTableProps()} style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            {table.headerGroups.map((hg) => (
              <tr {...hg.getHeaderGroupProps()} key={hg.id}>
                {hg.headers.map((col) => (
                  <th
                    {...col.getHeaderProps(col.getSortByToggleProps())}
                    key={col.id}
                    style={{
                      textAlign: "left",
                      padding: "10px 8px",
                      borderBottom: "1px solid rgba(0,0,0,0.12)",
                      fontSize: 12,
                      color: "#344767",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span>{col.render("Header")}</span>
                      <span style={{ fontSize: 11, opacity: 0.7 }}>
                        {col.isSorted ? (col.isSortedDesc ? "▼" : "▲") : ""}
                      </span>
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody {...table.getTableBodyProps()}>
            {table.rows.map((row) => {
              table.prepareRow(row);
              return (
                <tr
                  {...row.getRowProps()}
                  key={row.id}
                  style={{ borderBottom: "1px solid rgba(0,0,0,0.06)" }}
                >
                  {row.cells.map((cell) => (
                    <td
                      {...cell.getCellProps()}
                      key={cell.column.id}
                      style={{
                        padding: "10px 8px",
                        fontSize: 13,
                        color: "#344767",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {cell.render("Cell")}
                    </td>
                  ))}
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr>
                <td colSpan={columns.length} style={{ padding: 12, color: "#67748e" }}>
                  Sem dados de vendedores.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function VendasTable({ rows, nifByRef, maxHeight = 400 }) {
  const data = useMemo(() => (Array.isArray(rows) ? rows : []), [rows]);

  const totais = useMemo(() => {
    return {
      qtd: data.reduce((acc, r) => acc + Number(r?.qtd || 0), 0),
      euros: data.reduce((acc, r) => acc + Number(r?.euros || 0), 0),
      litros: data.reduce((acc, r) => acc + Number(r?.litros || 0), 0),
    };
  }, [data]);

  const columns = useMemo(
    () => [
      { Header: "Data", accessor: "faturadata", Filter: TextFilter },
      {
        Header: "Fatura",
        accessor: "faturanumero",
        Filter: TextFilter,
      },
      { Header: "Vendedor", accessor: "vendedor", Filter: TextFilter },
      {
        Header: "Cliente",
        accessor: "cliente",
        Filter: TextFilter,
        Cell: ({ row, value }) => {
          const ref = String(row.original?.refcliente || "").trim();
          const nif = nifByRef?.get(ref);

          return (
            <span>
              {value}
              {nif ? <span style={{ color: "#67748e" }}> ({nif})</span> : ""}
            </span>
          );
        },
      },
      { Header: "Material", accessor: "material", Filter: TextFilter },
      { Header: "Fabricante", accessor: "fabricante", Filter: TextFilter },
      { Header: "Família", accessor: "familia", Filter: TextFilter },
      { Header: "Marca", accessor: "marca", Filter: TextFilter },
      { Header: "Submarca", accessor: "submarca", Filter: TextFilter },
      { Header: "Embalagem", accessor: "embalagem", Filter: TextFilter },

      { Header: "Seg. Pot.", accessor: "segmentopotencial", Filter: TextFilter },
      { Header: "Seg. N1", accessor: "segmenton1", Filter: TextFilter },
      { Header: "Seg. N2", accessor: "segmenton2", Filter: TextFilter },
      { Header: "Seg. N3", accessor: "segmenton3", Filter: TextFilter },

      { Header: "Grupo", accessor: "grupoclientes", Filter: TextFilter },
      { Header: "Bloq. Forn.", accessor: "bloqueiofornecimento", Filter: TextFilter },
      { Header: "Bloq. Fat.", accessor: "bloqueiofaturacao", Filter: TextFilter },
      { Header: "Bloq. Ordem", accessor: "bloqueioordem", Filter: TextFilter },

      {
        Header: () => (
          <div>
            <div
              style={{
                textAlign: "center",
              }}
            >
              Qtd
            </div>
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "#344767",
                marginTop: 2,
              }}
            >
              {numFmt(totais.qtd)}
            </div>
          </div>
        ),
        accessor: "qtd",
        disableFilters: true,
        Cell: ({ value }) => numFmt(value ?? 0),
      },
      {
        Header: () => (
          <div>
            <div
              style={{
                textAlign: "center",
              }}
            >
              €
            </div>
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "#344767",
                marginTop: 2,
              }}
            >
              {numFmt(totais.euros)}
            </div>
          </div>
        ),
        accessor: "euros",
        disableFilters: true,
        Cell: ({ value }) => numFmt(value ?? 0),
      },
      {
        Header: () => (
          <div>
            <div
              style={{
                textAlign: "center",
              }}
            >
              L
            </div>
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "#344767",
                marginTop: 2,
              }}
            >
              {numFmt(totais.litros)}
            </div>
          </div>
        ),
        accessor: "litros",
        disableFilters: true,
        Cell: ({ value }) => numFmt(value ?? 0),
      },
    ],
    [totais, nifByRef]
  );

  const table = useTable({ columns, data, initialState: {} }, useFilters, useSortBy);
  const { getTableProps, getTableBodyProps, headerGroups, rows: tableRows, prepareRow } = table;

  // refs (vertical / horizontal interno / barra visível)
  const vRef = useRef(null);
  const hRef = useRef(null);
  const hbarRef = useRef(null);
  const tableRef = useRef(null);

  const syncBarWidth = () => {
    const hb = hbarRef.current;
    const tb = tableRef.current;
    if (!hb || !tb) return;

    const w = tb.scrollWidth || 0;
    const spacer = hb.firstChild;
    if (spacer) spacer.style.width = `${w}px`;
  };

  useEffect(() => {
    syncBarWidth();
    const onResize = () => syncBarWidth();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [data, columns]);

  useEffect(() => {
    const h = hRef.current;
    const hb = hbarRef.current;
    if (!h || !hb) return;

    let lock = false;

    const onScrollH = () => {
      if (lock) return;
      lock = true;
      hb.scrollLeft = h.scrollLeft;
      lock = false;
    };

    const onScrollBar = () => {
      if (lock) return;
      lock = true;
      h.scrollLeft = hb.scrollLeft;
      lock = false;
    };

    h.addEventListener("scroll", onScrollH, { passive: true });
    hb.addEventListener("scroll", onScrollBar, { passive: true });

    return () => {
      h.removeEventListener("scroll", onScrollH);
      hb.removeEventListener("scroll", onScrollBar);
    };
  }, []);

  const thBase = {
    textAlign: "left",
    padding: "10px 8px",
    borderBottom: "1px solid rgba(0,0,0,0.12)",
    fontSize: 12,
    color: "#344767",
    whiteSpace: "nowrap",
    verticalAlign: "bottom",
    background: "#fff",
    zIndex: 3,
  };

  return (
    <div
      style={{
        width: "100%",
        border: "1px solid rgba(0,0,0,0.08)",
        borderRadius: 8,
        background: "#fff",
        overflow: "hidden",
      }}
    >
      {/* CSS local para esconder a barra horizontal interna (fica só a de baixo) */}
      <style>{`
        .hScrollNoBar { scrollbar-width: none; -ms-overflow-style: none; }
        .hScrollNoBar::-webkit-scrollbar { height: 0px; }
      `}</style>

      {/* SCROLL VERTICAL (sem barra horizontal) */}
      <div
        ref={vRef}
        style={{
          maxHeight,
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >
        {/* SCROLL HORIZONTAL (barra escondida) */}
        <div
          ref={hRef}
          className="hScrollNoBar"
          style={{
            overflowX: "auto",
            overflowY: "hidden",
          }}
        >
          <table
            {...getTableProps()}
            ref={tableRef}
            style={{
              width: "max-content",
              minWidth: "100%",
              borderCollapse: "collapse",
            }}
          >
            <thead>
              {headerGroups.map((hg) => (
                <tr {...hg.getHeaderGroupProps()} key={hg.id}>
                  {hg.headers.map((col) => (
                    <th
                      {...col.getHeaderProps(col.getSortByToggleProps())}
                      key={col.id}
                      style={{
                        ...thBase,
                        position: "sticky",
                        top: 0,
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <span>{col.render("Header")}</span>
                        <span style={{ fontSize: 11, opacity: 0.7 }}>
                          {col.isSorted ? (col.isSortedDesc ? "▼" : "▲") : ""}
                        </span>
                      </div>

                      {!col.disableFilters && (
                        <div style={{ marginTop: 6 }}>{col.render("Filter")}</div>
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>

            <tbody {...getTableBodyProps()}>
              {tableRows.map((row) => {
                prepareRow(row);
                return (
                  <tr
                    {...row.getRowProps()}
                    key={row.id}
                    style={{ borderBottom: "1px solid rgba(0,0,0,0.05)" }}
                  >
                    {row.cells.map((cell) => (
                      <td
                        {...cell.getCellProps()}
                        key={cell.column.id}
                        style={{
                          padding: "10px 8px",
                          fontSize: 13,
                          color: "#344767",
                          whiteSpace: "nowrap",
                          verticalAlign: "middle",
                        }}
                      >
                        {cell.render("Cell")}
                      </td>
                    ))}
                  </tr>
                );
              })}

              {tableRows.length === 0 && (
                <tr>
                  <td colSpan={columns.length} style={{ padding: 16, color: "#67748e" }}>
                    Sem vendas.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* BARRA HORIZONTAL ÚNICA (visível, em baixo) */}
      <div
        ref={hbarRef}
        style={{
          height: 14,
          overflowX: "scroll",
          overflowY: "hidden",
          borderTop: "1px solid rgba(0,0,0,0.08)",
          background: "#fff",
        }}
      >
        <div style={{ height: 1, width: 0 }} />
      </div>
    </div>
  );
}

export default function Objetivos() {
  const { showAlert } = useAlert();
  const { temPermissao, temFuncao, userData } = useAuth();

  const refUser = userData?.refvendedor || null;

  // “Admin-like” (ajusta se tiveres mais perfis)
  const isAdminLike =
    temFuncao("Administração") ||
    temFuncao("Informática") ||
    temFuncao("Tesouraria") ||
    temFuncao("Gestão Objetivos");

  // Comercial restrito = vendedor “normal”
  const isComercialRestrito = temFuncao("Comercial") && !isAdminLike;

  const TABLE_KEY = "objetivos";
  const LS_FILTERS_KEY = `tbl:${TABLE_KEY}:filters`;
  const LS_SORT_KEY = `tbl:${TABLE_KEY}:sortBy`;
  const LS_KEEP_KEY = `tbl:${TABLE_KEY}:keep`;

  const [keepFilters, setKeepFilters] = useState(() => localStorage.getItem(LS_KEEP_KEY) !== "0");

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [openDel, setOpenDel] = useState(false);
  const [objDel, setObjDel] = useState(null);
  const [delLoading, setDelLoading] = useState(false);

  // Dialog editor
  const [openEditor, setOpenEditor] = useState(false);
  const [idEdit, setIdEdit] = useState(null);

  const [openDetalhes, setOpenDetalhes] = useState(false);
  const [detLoading, setDetLoading] = useState(false);
  const [detObj, setDetObj] = useState(null);
  const [detData, setDetData] = useState(null);

  // Dialog “Vendas do Objetivo”
  const [openVendas, setOpenVendas] = useState(false);
  const [vendasLoading, setVendasLoading] = useState(false);
  const [vendasRows, setVendasRows] = useState([]);

  const [loadingExcelVendas, setLoadingExcelVendas] = useState(false);
  const [vendasRefvendedorFiltro, setVendasRefvendedorFiltro] = useState(null);

  // NIF por refcliente (para "Clientes distintos")
  const [nifByRef, setNifByRef] = useState(new Map());

  const individuaisVendedoresVisiveis = useMemo(() => {
    const arr = detData?.individuais_vendedores || [];

    if (isComercialRestrito) {
      if (!refUser) return [];
      return arr.filter((v) => String(v?.refvendedor) === String(refUser));
    }

    return arr;
  }, [detData, isComercialRestrito, refUser]);

  // Dialog detalhe "Real" da Equipa (por vendedor)
  const [openEqReal, setOpenEqReal] = useState(false);
  const [eqRealLoading, setEqRealLoading] = useState(false);
  const [eqRealTipoKey, setEqRealTipoKey] = useState(null); // "qtd"|"euros"|"litros"|"clientes"
  const [eqRealTipoLabel, setEqRealTipoLabel] = useState("");
  const [eqRealRows, setEqRealRows] = useState([]);

  const upsertObjetivoNaTabela = async (idObjetivo, abrirEditorDepois = false) => {
    if (!idObjetivo) return;

    setLoading(true);

    try {
      const [objCache, err] = await handleApi(
        api.post("Comercial/SelObjetivoCache", { id: idObjetivo })
      );

      if (err) {
        showAlert(err, "error");
        return;
      }

      if (!objCache || !objCache.objetivo) {
        await carregar();
        return;
      }

      const enriched = await enriquecerObjetivoCache(objCache.objetivo);

      if (!enriched) {
        await carregar();
        return;
      }

      setData((prev) => {
        const arr = Array.isArray(prev) ? [...prev] : [];
        const idx = arr.findIndex((x) => Number(x?.id) === Number(idObjetivo));

        if (idx >= 0) {
          arr[idx] = enriched;
        } else {
          arr.unshift(enriched);
        }

        arr.sort((a, b) => {
          const da = String(a?.dataini || "");
          const db = String(b?.dataini || "");

          if (da !== db) return db.localeCompare(da);
          return Number(b?.id || 0) - Number(a?.id || 0);
        });

        return arr;
      });

      if (abrirEditorDepois) {
        setIdEdit(idObjetivo);
        setOpenEditor(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const removerObjetivoDaTabela = (idObjetivo) => {
    setData((prev) =>
      (Array.isArray(prev) ? prev : []).filter((x) => Number(x?.id) !== Number(idObjetivo))
    );
  };

  const abrirDetalhes = async (obj) => {
    if (!obj?.id) return;

    setDetObj(obj);
    setDetData(null);
    setOpenDetalhes(true);

    setDetLoading(true);
    const [payload, err] = await handleApi(
      api.post("Comercial/SelObjetivoDetalhes", { id: obj.id })
    );
    setDetLoading(false);

    if (err) {
      showAlert(err, "error");
      return;
    }

    //console.log("SelObjetivoDetalhes payload", payload);

    setDetData(payload || null);
  };

  const abrirDetalheRealEquipa = async (tipoKey, tipoLabel) => {
    if (!detObj?.id) return;

    // Comercial restrito: tem de ter refvendedor para filtrar a sua linha
    if (isComercialRestrito && !refUser) {
      showAlert("Não foi possível identificar o seu vendedor (refvendedor).", "error");
      return;
    }

    setEqRealTipoKey(tipoKey);
    setEqRealTipoLabel(tipoLabel || "");
    setEqRealRows([]);
    setOpenEqReal(true);

    setEqRealLoading(true);
    const [payload, err] = await handleApi(
      api.post("Comercial/SelObjetivoEquipaDetalheVendedores", {
        id: detObj.id,
        tipo: tipoKey,
      })
    );

    // console.log("SelObjetivoEquipaDetalheVendedores payload", payload);

    setEqRealLoading(false);

    if (err) {
      showAlert(err, "error");
      return;
    }

    let rows = Array.isArray(payload) ? payload : [];

    // ✅ Comercial: mostrar apenas a sua linha (mesmo que a API devolva todos)
    if (isComercialRestrito && refUser) {
      rows = rows.filter((r) => String(r?.refvendedor) === String(refUser));
    }

    setEqRealRows(rows);
  };

  const abrirVendasObjetivo = async (refvendedorFiltro = null) => {
    if (!detObj?.id) return;

    setVendasRefvendedorFiltro(refvendedorFiltro || null);

    setOpenVendas(true);
    setVendasRows([]);
    setNifByRef(new Map());
    setVendasLoading(true);

    try {
      const [payload, err] = await handleApi(
        api.post("Comercial/SelObjetivoVendas", {
          id: detObj.id,
          idutilizador: userData.id,
          isadminlike: isAdminLike,
        })
      );

      if (err) {
        showAlert(err, "error");
        return;
      }

      let rows = Array.isArray(payload) ? payload : [];

      if (refvendedorFiltro) {
        rows = rows.filter(
          (r) => String(r?.refvendedor || "").trim() === String(refvendedorFiltro).trim()
        );
      }

      setVendasRows(rows);

      const refs = Array.from(
        new Set(rows.map((r) => String(r?.refcliente || "").trim()).filter(Boolean))
      );

      if (refs.length) {
        const [nifs, errNif] = await handleApi(
          api.post("Comercial/SelEntidadesNifPorRefs", { refs })
        );

        if (!errNif && Array.isArray(nifs)) {
          const m = new Map();
          nifs.forEach((x) => {
            const ref = String(x?.ref || "").trim();
            const nif = String(x?.nif || "").trim();
            if (ref) m.set(ref, nif);
          });
          setNifByRef(m);
        }
      }
    } finally {
      setVendasLoading(false);
    }
  };

  const podeGerir =
    temFuncao("Administração") || temFuncao("Informática") || temFuncao("Gestão Objetivos");

  const pedirApagar = (obj) => {
    if (!podeGerir) return;
    setObjDel(obj);
    setOpenDel(true);
  };

  const confirmarApagar = async () => {
    if (!podeGerir || !objDel?.id) return;

    setDelLoading(true);

    try {
      const idApagado = Number(objDel.id);

      const [_, err, meta] = await handleApi(api.post("Comercial/DelObjetivo", { id: idApagado }));

      if (err) {
        showAlert(err, "error");
        return;
      }

      if (idApagado > 0) {
        const [_rebuildPayload, rebuildErr] = await handleApi(
          api.post("Sincronizacao/objetivos/rebuildcacheobjetivo", {
            idobjetivo: idApagado,
          })
        );

        if (rebuildErr) {
          showAlert(rebuildErr, "error");
          return;
        }
      }

      removerObjetivoDaTabela(idApagado);

      showAlert(meta?.message ?? "Objetivo apagado.", meta?.severity ?? "success");
      setOpenDel(false);
      setObjDel(null);
    } finally {
      setDelLoading(false);
    }
  };

  const carregar = async () => {
    setLoading(true);

    try {
      const [rows, err] = await handleApi(api.post("Comercial/SelObjetivosCache", {}));

      if (err) {
        showAlert(err, "error");
        setData([]);
        return;
      }

      const list = Array.isArray(rows) ? rows : [];

      const enriched = list.map((o) => {
        const met = pickMetricKey(o);

        return {
          ...o,
          _metricKey: met,
        };
      });

      setData(enriched);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      await carregar();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const copiarObjetivo = async (obj) => {
    if (!obj?.id) return;

    setLoading(true);

    try {
      const [payload, err, meta] = await handleApi(
        api.post("Comercial/CopiarObjetivo", { id: obj.id })
      );

      if (err) {
        showAlert(err, "error");
        return;
      }

      const novoId = Number(payload?.id || payload?.data?.id || 0);

      if (novoId > 0) {
        const [_rebuildPayload, rebuildErr] = await handleApi(
          api.post("Sincronizacao/objetivos/rebuildcacheobjetivo", {
            idobjetivo: novoId,
          })
        );

        if (rebuildErr) {
          showAlert(rebuildErr, "error");
          return;
        }
      }

      showAlert(meta?.message ?? "Objetivo copiado.", meta?.severity ?? "success");

      if (novoId > 0) {
        await upsertObjetivoNaTabela(novoId, true);
        return;
      }

      await carregar();
    } finally {
      setLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: "",
        id: "acoes",
        disableFilters: true,
        width: 90,
        minWidth: 80,
        maxWidth: 110,
        Cell: ({ row }) => {
          const o = row.original;

          return (
            <div style={{ display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
              <MDButton
                variant="outlined"
                color="info"
                size="small"
                onClick={() => abrirDetalhes(o)}
                sx={{
                  fontSize: 11,
                  textTransform: "none",
                  padding: "2px 8px",
                  minWidth: "auto",
                }}
              >
                Detalhes
              </MDButton>

              {podeGerir && (
                <>
                  <IconButton
                    size="small"
                    color="primary"
                    title="Editar"
                    onClick={() => {
                      setIdEdit(o.id);
                      setOpenEditor(true);
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>

                  <IconButton
                    size="small"
                    color="error"
                    title="Apagar"
                    onClick={() => pedirApagar(o)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>

                  <IconButton
                    size="small"
                    color="success"
                    title="Copiar objetivo"
                    onClick={() => copiarObjetivo(row.original)}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </>
              )}
            </div>
          );
        },
      },

      // NOTA: removemos a coluna ID da tabela (não aparece)

      {
        Header: "Nome",
        accessor: "objetivo_nome",
        Filter: TextFilter,
        Cell: ({ value, row }) => {
          const o = row.original;
          const k = o?._metricKey || pickMetricKey(o);
          return `${value} (${metricLabel(k)})`;
        },
      },

      {
        Header: "Início",
        accessor: "dataini",
        Filter: TextFilter,
        Cell: ({ value }) => dateOnly(value),
      },
      {
        Header: "Fim",
        accessor: "datafim",
        Filter: TextFilter,
        Cell: ({ value }) => dateOnly(value),
      },

      {
        Header: "Fabricantes",
        accessor: "fabricantes_nomes",
        Filter: TextFilter,
        Cell: ({ value }) => <TruncTooltipCell value={value} maxWidth={260} />,
      },
      {
        Header: "Famílias",
        accessor: "familias_nomes",
        Filter: TextFilter,
        Cell: ({ value }) => <TruncTooltipCell value={value} maxWidth={240} />,
      },

      //   {
      //     Header: "Valores (fabricante)",
      //     id: "valores",
      //     disableFilters: true,
      //     Cell: ({ row }) => (
      //       <span title={valoresResumo(row.original)} style={{ fontSize: 13 }}>
      //         {valoresResumo(row.original)}
      //       </span>
      //     ),
      //   },
    ],
    [temPermissao, temFuncao, podeGerir] // importante
  );

  const savedFilters = useMemo(() => {
    try {
      const raw = localStorage.getItem(LS_FILTERS_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }, []);

  const savedSortBy = useMemo(() => {
    try {
      const raw = localStorage.getItem(LS_SORT_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }, []);

  const tableInstance = useTable(
    {
      columns,
      data,
      initialState: {
        filters: savedFilters,
        sortBy: savedSortBy,
      },
      autoResetFilters: false,
      autoResetSortBy: false,
    },
    useFilters,
    useSortBy
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    state: tableState,
    setAllFilters,
    setSortBy,
  } = tableInstance;

  useEffect(() => {
    if (!keepFilters) return;

    try {
      localStorage.setItem(LS_FILTERS_KEY, JSON.stringify(tableState.filters || []));

      localStorage.setItem(LS_SORT_KEY, JSON.stringify(tableState.sortBy || []));

      localStorage.setItem(LS_KEEP_KEY, "1");
    } catch {}
  }, [tableState.filters, tableState.sortBy, keepFilters]);

  useEffect(() => {
    if (!keepFilters) {
      try {
        localStorage.removeItem(LS_FILTERS_KEY);
        localStorage.removeItem(LS_SORT_KEY);
        localStorage.setItem(LS_KEEP_KEY, "0");
      } catch {}
    }
  }, [keepFilters]);

  const abrirNovo = () => {
    setIdEdit(null);
    setOpenEditor(true);
  };

  const exportarVendasExcel = async () => {
    if (!detObj?.id) return;

    try {
      setLoadingExcelVendas(true);

      const res = await api({
        url: "Comercial/ExportarObjetivoVendas",
        method: "post",
        data: {
          id: detObj.id,
          idutilizador: userData.id,
          isadminlike: isAdminLike,
          refvendedor: vendasRefvendedorFiltro || "",
        },
        responseType: "arraybuffer",
      });

      const contentDisposition = res.headers["content-disposition"] || "";

      let fileName = `Objetivo_Vendas_${detObj.id}.xlsx`;

      const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
      const normalMatch = contentDisposition.match(/filename="?([^"]+)"?/i);

      if (utf8Match?.[1]) {
        fileName = decodeURIComponent(utf8Match[1]);
      } else if (normalMatch?.[1]) {
        fileName = normalMatch[1];
      }

      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");

      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();

      a.remove();
      window.URL.revokeObjectURL(url);

      showAlert("Excel gerado com sucesso.", "success");
    } catch (err) {
      showAlert("Erro ao gerar Excel.", "error");
    } finally {
      setLoadingExcelVendas(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <PageHeader />

      <MDBox mt={2}>
        <Card>
          <MDBox
            p={2}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={2}
          >
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={keepFilters}
                  onChange={(e) => {
                    const checked = e.target.checked;

                    setKeepFilters(checked);

                    if (!checked) {
                      setAllFilters([]);
                      setSortBy([]);
                    }
                  }}
                />
              }
              label={<span style={{ fontSize: 12 }}>Manter filtros da tabela</span>}
              sx={{ m: 0 }}
            />

            <MDButton
              color="success"
              variant="contained"
              onClick={abrirNovo}
              disabled={!isAdminLike}
            >
              Novo Objetivo
            </MDButton>
          </MDBox>

          <MDBox p={2} pt={0}>
            {loading ? (
              <MDBox display="flex" alignItems="center" justifyContent="center" py={6}>
                <CircularProgress />
              </MDBox>
            ) : (
              <div style={{ width: "100%", overflowX: "auto" }}>
                <table {...getTableProps()} style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    {headerGroups.map((hg) => (
                      <tr {...hg.getHeaderGroupProps()} key={hg.id}>
                        {hg.headers.map((col) => (
                          <th
                            {...col.getHeaderProps(col.getSortByToggleProps())}
                            key={col.id}
                            style={{
                              textAlign: "left",
                              padding: "10px 8px",
                              borderBottom: "1px solid rgba(0,0,0,0.08)",
                              fontSize: 12,
                              color: "#344767",
                              whiteSpace: "nowrap",
                            }}
                          >
                            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                              <span>{col.render("Header")}</span>
                              <span style={{ fontSize: 11, opacity: 0.7 }}>
                                {col.isSorted ? (col.isSortedDesc ? "▼" : "▲") : ""}
                              </span>
                            </div>

                            {!col.disableFilters && (
                              <div style={{ marginTop: 6 }}>{col.render("Filter")}</div>
                            )}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>

                  <tbody {...getTableBodyProps()}>
                    {rows.map((row) => {
                      prepareRow(row);
                      return (
                        <tr
                          {...row.getRowProps()}
                          key={row.id}
                          style={{
                            borderBottom: "1px solid rgba(0,0,0,0.05)",
                          }}
                        >
                          {row.cells.map((cell) => (
                            <td
                              {...cell.getCellProps()}
                              key={cell.column.id}
                              style={{
                                padding: "10px 8px",
                                fontSize: 13,
                                color: "#344767",
                                whiteSpace: "nowrap",
                                verticalAlign: "middle",
                              }}
                            >
                              {cell.render("Cell")}
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                    {rows.length === 0 && (
                      <tr>
                        <td colSpan={columns.length} style={{ padding: 16, color: "#67748e" }}>
                          Sem dados.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </MDBox>
        </Card>
      </MDBox>

      {/* Editor em Dialog */}
      <Dialog
        open={openEditor}
        onClose={() => setOpenEditor(false)}
        fullWidth
        maxWidth="xl"
        PaperProps={{
          sx: {
            borderRadius: 2,
            overflow: "hidden",
            height: "92vh",
            maxHeight: "92vh",
          },
        }}
        PaperComponent={PaperComponent}
      >
        <DialogTitle
          className="draggable-dialog-title"
          style={{ cursor: "move" }}
          sx={{ py: 1.5, px: 2 }}
        >
          {idEdit ? "Editar Objetivo" : "Novo Objetivo"}
        </DialogTitle>

        <DialogContent sx={{ p: 0, height: "100%", overflow: "auto" }}>
          <CriarObjetivos
            embed
            idObjetivo={idEdit}
            onClose={() => setOpenEditor(false)}
            onSaved={async (payload) => {
              const id = Number(payload?.id || payload?.data?.id || idEdit || 0);

              setOpenEditor(false);

              if (id > 0) {
                const [_, rebuildErr] = await handleApi(
                  api.post("Sincronizacao/objetivos/rebuildcacheobjetivo", {
                    idobjetivo: id,
                  })
                );

                if (rebuildErr) {
                  showAlert(rebuildErr, "error");
                  return;
                }

                await carregar();
                return;
              }

              await carregar();
            }}
          />
        </DialogContent>
      </Dialog>

      <Dialog
        open={openDel}
        onClose={() => (delLoading ? null : setOpenDel(false))}
        maxWidth="sm"
        fullWidth
        PaperComponent={PaperComponent}
      >
        <DialogTitle className="draggable-dialog-title" style={{ cursor: "move" }}>
          Apagar objetivo
        </DialogTitle>

        <DialogContent dividers>
          <MDTypography variant="body2">
            Tens a certeza que queres apagar o objetivo <b>{objDel?.objetivo_nome}</b> (início:{" "}
            <b>{objDel?.dataini?.slice(0, 10)}</b>)?
          </MDTypography>

          <MDBox mt={2}>
            <MDTypography variant="body2" color="text">
              Isto remove também famílias, fabricantes, equipa, vendedores, clientes, etc.
            </MDTypography>
          </MDBox>
        </DialogContent>

        <DialogActions>
          <MDButton color="secondary" onClick={() => setOpenDel(false)} disabled={delLoading}>
            Cancelar
          </MDButton>

          <MDButton color="error" onClick={confirmarApagar} disabled={delLoading}>
            {delLoading ? "A apagar..." : "Apagar"}
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openDetalhes}
        onClose={() => setOpenDetalhes(false)}
        maxWidth="md"
        fullWidth
        PaperComponent={PaperComponent}
      >
        <DialogTitle className="draggable-dialog-title" style={{ cursor: "move" }}>
          Detalhes — {detObj?.objetivo_nome || `Objetivo #${detObj?.id || ""}`}
        </DialogTitle>

        <DialogContent dividers>
          {detLoading && (
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: 12 }}>
              <CircularProgress size={20} />
              <span>A calcular diferenças…</span>
            </div>
          )}

          {!detLoading && !detData && <div style={{ padding: 12, opacity: 0.8 }}>Sem dados.</div>}

          {!detLoading && detData && (
            <div>
              {!isComercialRestrito && hasAnyObjectiveSec(detData?.fabricante) && (
                <>
                  <SecResumoTable title="Objetivos do Fabricante" secData={detData?.fabricante} />
                  <Divider sx={{ my: 2 }} />
                </>
              )}

              {hasAnyObjectiveSec(detData?.equipa) && (
                <>
                  <SecResumoTable
                    title="Objetivos da Equipa"
                    secData={detData?.equipa}
                    enableRealClick={!isComercialRestrito || !!refUser}
                    onRealClick={abrirDetalheRealEquipa}
                  />

                  <Divider sx={{ my: 2 }} />
                </>
              )}

              {!isComercialRestrito && hasAnyObjectiveSec(detData?.individuais) && (
                <>
                  <SecResumoTable
                    title="Objetivos Individuais (Totais)"
                    secData={detData?.individuais}
                  />
                  <Divider sx={{ my: 2 }} />
                </>
              )}

              {hasAnyObjectiveVendedores(individuaisVendedoresVisiveis, "qtd") && (
                <>
                  <MDTypography variant="button" fontWeight="medium" sx={{ mb: 1 }}>
                    Individuais por Vendedor — Quantidades
                  </MDTypography>

                  <VendedoresTable
                    vendedores={individuaisVendedoresVisiveis}
                    tipoKey="qtd"
                    onVerVendas={abrirVendasObjetivo}
                  />
                  <Divider sx={{ my: 2 }} />
                </>
              )}

              {hasAnyObjectiveVendedores(individuaisVendedoresVisiveis, "euros") && (
                <>
                  <MDTypography variant="button" fontWeight="medium" sx={{ mb: 1 }}>
                    Individuais por Vendedor — Euros
                  </MDTypography>

                  <VendedoresTable
                    vendedores={individuaisVendedoresVisiveis}
                    tipoKey="euros"
                    onVerVendas={abrirVendasObjetivo}
                  />
                  <Divider sx={{ my: 2 }} />
                </>
              )}

              {hasAnyObjectiveVendedores(individuaisVendedoresVisiveis, "litros") && (
                <>
                  <MDTypography variant="button" fontWeight="medium" sx={{ mb: 1 }}>
                    Individuais por Vendedor — Litros
                  </MDTypography>
                  <VendedoresTable
                    vendedores={individuaisVendedoresVisiveis}
                    tipoKey="litros"
                    onVerVendas={abrirVendasObjetivo}
                  />
                  <Divider sx={{ my: 2 }} />
                </>
              )}

              {hasAnyObjectiveVendedores(individuaisVendedoresVisiveis, "clientes") && (
                <>
                  <MDTypography variant="button" fontWeight="medium" sx={{ mb: 1 }}>
                    Individuais por Vendedor — Clientes
                  </MDTypography>

                  <VendedoresTable
                    vendedores={individuaisVendedoresVisiveis}
                    tipoKey="clientes"
                    onVerVendas={abrirVendasObjetivo}
                  />
                </>
              )}
            </div>
          )}
        </DialogContent>

        <DialogActions>
          {/* <MDButton color="info" onClick={abrirVendasObjetivo} disabled={detLoading || !detData}>
            Ver vendas
          </MDButton> */}

          <MDButton color="secondary" onClick={() => setOpenDetalhes(false)}>
            Fechar
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog
        PaperComponent={PaperComponent}
        open={openEqReal}
        onClose={() => setOpenEqReal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle className="draggable-dialog-title" style={{ cursor: "move" }}>
          Real da Equipa — {eqRealTipoLabel ? eqRealTipoLabel : ""} (por vendedor)
        </DialogTitle>

        <DialogContent dividers>
          {eqRealLoading ? (
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: 12 }}>
              <CircularProgress size={20} />
              <span>A carregar detalhe…</span>
            </div>
          ) : (
            <div style={{ width: "100%", overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th
                      style={{
                        textAlign: "left",
                        padding: "10px 8px",
                        borderBottom: "1px solid rgba(0,0,0,0.12)",
                      }}
                    >
                      Vendedor
                    </th>
                    <th
                      style={{
                        textAlign: "right",
                        padding: "10px 8px",
                        borderBottom: "1px solid rgba(0,0,0,0.12)",
                      }}
                    >
                      Valor
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {eqRealRows.map((r, idx) => (
                    <tr
                      key={`${r?.refvendedor || ""}_${idx}`}
                      style={{ borderBottom: "1px solid rgba(0,0,0,0.06)" }}
                    >
                      <td style={{ padding: "10px 8px", color: "#344767" }}>
                        <span title={r?.refvendedor || ""}>
                          {r?.vendedor || r?.refvendedor || ""}
                        </span>
                      </td>
                      <td
                        style={{
                          padding: "10px 8px",
                          textAlign: "right",
                          color: "#344767",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            alignItems: "center",
                            gap: 6,
                          }}
                        >
                          <strong>{numFmt(r?.valor)}</strong>

                          <Tooltip title="Ver vendas deste vendedor" arrow>
                            <IconButton
                              size="small"
                              color="info"
                              onClick={() => abrirVendasObjetivo(r?.refvendedor)}
                            >
                              <InfoOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </div>
                      </td>
                    </tr>
                  ))}

                  {eqRealRows.length === 0 && (
                    <tr>
                      <td colSpan={2} style={{ padding: 12, color: "#67748e" }}>
                        Sem dados.
                      </td>
                    </tr>
                  )}
                </tbody>

                {eqRealRows.length > 1 && (
                  <tfoot>
                    <tr>
                      <td style={{ padding: "10px 8px", borderTop: "1px solid rgba(0,0,0,0.12)" }}>
                        <strong>Total</strong>
                      </td>
                      <td
                        style={{
                          padding: "10px 8px",
                          textAlign: "right",
                          borderTop: "1px solid rgba(0,0,0,0.12)",
                        }}
                      >
                        <strong>
                          {numFmt(eqRealRows.reduce((acc, x) => acc + Number(x?.valor ?? 0), 0))}
                        </strong>
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}
        </DialogContent>

        <DialogActions>
          <MDButton color="secondary" onClick={() => setOpenEqReal(false)}>
            Fechar
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog
        PaperComponent={PaperComponent}
        open={openVendas}
        onClose={() => {
          setOpenVendas(false);
          setVendasRows([]);
          setNifByRef(new Map());
        }}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle className="draggable-dialog-title" style={{ cursor: "move" }}>
          Vendas — {detObj?.objetivo_nome || `Objetivo #${detObj?.id || ""}`}
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            overflowX: "hidden", // evita scroll duplo no Dialog
          }}
        >
          {vendasLoading ? (
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: 12 }}>
              <CircularProgress size={20} />
              <span>A carregar vendas…</span>
            </div>
          ) : (
            <>
              {/* CLIENTES DISTINCTOS (tabela pequena) */}
              {(() => {
                const map = new Map();
                (Array.isArray(vendasRows) ? vendasRows : []).forEach((r) => {
                  const k = String(r?.refcliente || "").trim();
                  if (!k) return;
                  if (!map.has(k)) map.set(k, { refcliente: k, cliente: r?.cliente || k });
                });
                const clientes = Array.from(map.values()).sort((a, b) =>
                  String(a.cliente || "").localeCompare(String(b.cliente || ""), "pt-PT", {
                    sensitivity: "base",
                  })
                );

                return (
                  <MDBox mb={2}>
                    <MDTypography variant="button" fontWeight="medium" sx={{ mb: 1 }}>
                      Clientes distintos ({clientes.length})
                    </MDTypography>

                    <div
                      style={{
                        width: "100%",
                        maxHeight: 220, // 👈 limite de altura (ajusta se quiseres)
                        overflowX: "auto", // raramente usado aqui, mas mantém coerência
                        overflowY: "auto", // scroll vertical
                        border: "1px solid rgba(0,0,0,0.08)",
                        borderRadius: 6,
                      }}
                    >
                      <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                          <tr>
                            <th
                              style={{
                                textAlign: "left",
                                padding: "8px 8px",
                                borderBottom: "1px solid rgba(0,0,0,0.12)",
                                fontSize: 12,
                              }}
                            >
                              Cliente
                            </th>
                            <th
                              style={{
                                textAlign: "left",
                                padding: "8px 8px",
                                borderBottom: "1px solid rgba(0,0,0,0.12)",
                                fontSize: 12,
                              }}
                            >
                              Ref
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {clientes.map((c) => (
                            <tr
                              key={c.refcliente}
                              style={{ borderBottom: "1px solid rgba(0,0,0,0.05)" }}
                            >
                              <td style={{ padding: "8px 8px", fontSize: 13, color: "#344767" }}>
                                {c.cliente}
                                {(() => {
                                  const nif =
                                    nifByRef && nifByRef.get ? nifByRef.get(c.refcliente) : "";

                                  return nif ? ` (${nif})` : "";
                                })()}
                              </td>

                              <td style={{ padding: "8px 8px", fontSize: 13, color: "#67748e" }}>
                                {c.refcliente}
                              </td>
                            </tr>
                          ))}
                          {clientes.length === 0 && (
                            <tr>
                              <td colSpan={2} style={{ padding: 12, color: "#67748e" }}>
                                Sem clientes.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </MDBox>
                );
              })()}

              {/* VENDAS (tabela grande) */}
              <MDTypography variant="button" fontWeight="medium" sx={{ mb: 1 }}>
                Vendas ({Array.isArray(vendasRows) ? vendasRows.length : 0})
              </MDTypography>

              <VendasTable rows={vendasRows} nifByRef={nifByRef} />
            </>
          )}
        </DialogContent>

        <DialogActions>
          <MDButton
            color="success"
            startIcon={
              loadingExcelVendas ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <TableViewIcon />
              )
            }
            onClick={exportarVendasExcel}
            disabled={loadingExcelVendas || vendasLoading}
          >
            {loadingExcelVendas ? "A gerar Excel..." : "Exportar Excel"}
          </MDButton>

          <MDButton
            color="secondary"
            onClick={() => {
              setOpenVendas(false);
              setVendasRows([]);
              setNifByRef(new Map());
            }}
          >
            Fechar
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}
